import React, { ReactNode, useMemo } from 'react';
import classNames from 'classnames';

// Components
import { IconButton } from '@/views/components/buttons';
import Select, { SelectItem } from '@/views/components/inputs/Select';

// Utils
import { formatMessage } from '@/utils/i18n';

export const UNSELECTED_ITEM_INDEX = -1;

type Props = {
  label: ReactNode;
  labelForA11y: string;
  icon?: string;
  items: SelectItem[];
  selectedItemIndex: number;
  onChange: (nextItemIndex: number) => void;
  onClear: () => void;
  onRemove?: () => void;
  disabled?: boolean;
  hasError?: boolean;
  'data-testid'?: string;
};

const AccountSetRow: React.FC<Props> = props => {
  const {
    label,
    labelForA11y,
    icon,
    items,
    selectedItemIndex,
    onChange,
    onClear,
    onRemove,
    disabled = false,
    hasError = false,
    'data-testid': dataTestId,
  } = props;

  const selectedItem = useMemo(() => {
    if (selectedItemIndex === UNSELECTED_ITEM_INDEX) return undefined;
    return items.find(i => i.value === String(selectedItemIndex));
  }, [items, selectedItemIndex]);

  const showClear = !disabled && selectedItemIndex !== UNSELECTED_ITEM_INDEX;
  const showRemove = !disabled && onRemove != null;

  return (
    <div className="account-set-row" data-testid={dataTestId}>
      <div className="account-set-row-label">
        {icon && <i className={icon} />}
        <span>{label}</span>
      </div>

      <i className="icon-arrow-right account-set-row-arrow" />

      <div className="account-set-row-controls">
        <Select
          className={classNames('account-set-row-select', { 'account-set-row-select--error': hasError })}
          label={labelForA11y}
          hideLabel
          items={items}
          selectedItem={selectedItem}
          disabled={disabled}
          onChange={changes => {
            const nextValue = changes.selectedItem?.value;
            if (nextValue == null) return;
            const nextItemIndex = Number.parseInt(nextValue, 10);
            if (Number.isNaN(nextItemIndex)) return;
            onChange(nextItemIndex);
          }}
        />

        {showClear && (
          <IconButton
            icon="ds-close"
            noBackground
            stopClickPropagation
            onClick={onClear}
            tooltip={formatMessage('Comparison.AccountSets.Row.ClearSelection')}
            tooltipClassName="custom-tooltip-v2"
            ariaLabel={formatMessage('Comparison.AccountSets.Row.ClearSelection')}
          />
        )}

        {showRemove && (
          <IconButton
            icon="ds-remove-circle-outline"
            noBackground
            stopClickPropagation
            tooltip={formatMessage('Common.Remove')}
            tooltipClassName="custom-tooltip-v2"
            onClick={onRemove}
            ariaLabel={formatMessage('Comparison.AccountSets.Row.RemoveOptionalRecommended')}
          />
        )}
      </div>
    </div>
  );
};

export default AccountSetRow;
